import React, { useEffect, useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useSearchParams, useNavigate, useParams } from 'react-router-dom'

import PolicyCard from '../cards/PolicyCard'
import PolicyModal from '../modals/PolicyModal'

import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Loading from '../loading/Loading'
import Pagination from '@mui/material/Pagination'

import ResourceSearchControls from '../search/ResourceSearchControls'

import { initializePolicies, searchPolicies } from '../../reducers/policiesReducer'
import { initializeTags } from '../../reducers/tagsReducers'
import { setErrorNotification } from '../../reducers/notificationReducer'

import { buildQuery, buildSearchQuery } from '../../services/utils.js/query'
import { PAGES } from '../navigation/routes'
import PolicyList from "../lists/PolicyList";
import { SearchStyle } from '../styles'
import resourcesService from '../../services/resourceService'


const Policies = () => {
    const { id: openResourceId } = useParams()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const [resourcesToShow, setResourcesToShow] = useState(null)
    const [list, setList] = useState(true)

    const [start, setStart] = useState('')
    const [end, setEnd] = useState('')
    const [title, setTitle] = useState('')
    const [contentTags, setContentTags] = useState([])
    const [medicalTags, setMedicalTags] = useState([])

    const [linkedResource, setLinkedResource] = useState(null)


    const user = useSelector(state => state.user)
    const policiesData = useSelector(state => state.policies)
    const policies = useMemo(() => policiesData?.results || [], [policiesData?.results])
    const totalPages = policiesData?.total_pages || 1
    const isLoading = policiesData?.loading || false
    const tagsByType = useSelector(state => state.tags || {})
    const dispatch = useDispatch()


    const [page, setPage] = useState(1)

    useEffect(() => {
        if(searchParams.toString()) {
            let tagsParam = searchParams.get('tags')
            let startValue = searchParams.get('start_date')
            let endValue = searchParams.get('end_date')
            let titleValue = searchParams.get('title')
            let pageValue = parseInt(searchParams.get('page')) || 1

            if(startValue) setStart(startValue)
            if(endValue) setEnd(endValue)
            if(titleValue) setTitle(titleValue)
            setPage(pageValue)

            const orderingValue = searchParams.get('ordering')
            const query = buildSearchQuery(startValue, endValue, titleValue, tagsParam, orderingValue)

            dispatch(searchPolicies(query, pageValue))
        } else {
            const pageValue = parseInt(searchParams.get('page')) || 1
            setPage(pageValue)
            dispatch(initializePolicies(pageValue))
        }
    }, [searchParams, user, dispatch])

    // Map tag names from the query to tag IDs for the selectors. Kept apart from
    // the fetch effect so tags loading never refetches the list.
    useEffect(() => {
        if (!searchParams.toString()) return
        const tagsParam = searchParams.get('tags')
        const contentSelected = []
        const medicalSelected = []
        if (tagsParam) {
            tagsParam.split(',').forEach(name => {
                const contentTag = tagsByType?.content?.find(t => t.name === name)
                if (contentTag) contentSelected.push(String(contentTag.id))
                const medTag = tagsByType?.medicalSpecialty?.find(t => t.name === name)
                if (medTag) medicalSelected.push(String(medTag.id))
            })
        }
        setContentTags(contentSelected)
        setMedicalTags(medicalSelected)
    }, [searchParams, tagsByType])

    useEffect(() => {
        if (tagsByType?.default === undefined) dispatch(initializeTags('default'))
        if (tagsByType?.content === undefined) dispatch(initializeTags('content'))
        if (tagsByType?.medicalSpecialty === undefined) dispatch(initializeTags('medicalSpecialty'))
    }, [dispatch, tagsByType])

    useEffect(() => {
        let isActive = true

        if (openResourceId) {
            resourcesService.retrieveSingle(openResourceId)
                .then(data => {
                    if (!isActive) return
                    setLinkedResource(data)
                })
                .catch(() => {
                    if (!isActive) return
                    dispatch(setErrorNotification('Resource not found'))
                    navigate(PAGES.policies.path, { replace: true })
                })
        } else {
            setLinkedResource(null)
        }
        return () => {
            isActive = false
        }
    }, [openResourceId, dispatch, navigate])


    useEffect(() => {
        if(policies && policies.length > 0) {
            const r = (user && user.staff) ? policies.filter(resource => resource.approved) : policies
            setResourcesToShow(r)
        } else if (!isLoading) {
            setResourcesToShow([])
        }
    }, [policies, user, isLoading])


    const handleSubmit = (event) => {
        if (event && event.preventDefault) event.preventDefault()

        const startValue = start || null
        const endValue = end || null
        const titleValue = title || null

        // Map selected tag IDs back to tag names for the query
        const contentNames = (contentTags || []).map(id => tagsByType?.content?.find(t => String(t.id) === id)?.name).filter(Boolean)
        const medicalNames = (medicalTags || []).map(id => tagsByType?.medicalSpecialty?.find(t => String(t.id) === id)?.name).filter(Boolean)
        const combinedNames = [...contentNames, ...medicalNames]
        const tagsParam = combinedNames.length > 0 ? combinedNames : null

        const orderingValue = searchParams.get('ordering')
        const query = buildQuery(startValue, endValue, titleValue, tagsParam, orderingValue)
        const currentQuery = buildSearchQuery(searchParams.get('start_date'), searchParams.get('end_date'), searchParams.get('title'), searchParams.get('tags'), orderingValue)

        if(query !== currentQuery) {
            navigate(`${PAGES.policies.path}?${query}`)
        }
    }


    const handlePageChange = (event, value) => {
        const currentParams = new URLSearchParams(searchParams.toString())
        currentParams.set('page', value.toString())
        navigate(`${PAGES.policies.path}?${currentParams.toString()}`)
    }

    return (
        <Container sx={SearchStyle.container}>
            <Typography {...SearchStyle.title} gutterBottom >
                Policy and Regulatory Guidelines
            </Typography>
            <ResourceSearchControls
                title={title}
                setTitle={setTitle}
                start={start}
                setStart={setStart}
                end={end}
                setEnd={setEnd}
                contentTags={contentTags}
                setContentTags={setContentTags}
                medicalTags={medicalTags}
                setMedicalTags={setMedicalTags}
                isLoading={isLoading}
                onSubmit={handleSubmit}
                sortOptions={[]}
                list={list}
                setList={setList}
                compact
            />


            {linkedResource && (
                <PolicyModal
                    {...linkedResource}
                    initialOpen={true}
                    onExternalClose={() => navigate(PAGES.policies.path, { replace: true })}
                />
            )}

            {isLoading && <Loading />}
            {!isLoading && ((resourcesToShow && resourcesToShow.length > 0)?
                <>
                    <Grid container spacing={2}>
                        {list? resourcesToShow.map(policy =>
                            <Grid key={policy.id} item xs={12} >
                                <PolicyList {...policy}/>
                            </Grid>
                        ) : resourcesToShow.map(policy =>
                            <Grid key={policy.id} item xs={12} md={4} sx={SearchStyle.card}>
                                <PolicyCard {...policy}/>
                            </Grid>
                        )}
                    </Grid>
                    <Pagination sx={SearchStyle.pagination} count={totalPages} page={page} onChange={handlePageChange} />
                </> :
                <Typography {...SearchStyle.noResources.props} gutterBottom sx={SearchStyle.noResources.style}>
                    No Resources Found
                </Typography>)
            }
            <Box sx={{textAlign: 'center', padding: '10px'}}>
                <Typography variant='overline' component='p'>
                    All resources provided are strictly owned and distributed by the creators indicated on the resource
                </Typography>
            </Box>
        </Container>
    )
}

export default Policies
